import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, Stack, Typography, TextField, Button, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  CircularProgress, Alert, Tooltip, Chip, useMediaQuery, IconButton,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Restaurant as MealIcon, CheckCircle as CheckIcon, Paid as ValueIcon,
  Groups as CreditorsIcon, Send as SubmitIcon, Info as InfoIcon,
  CalendarMonth as DateIcon, Refresh as RefreshIcon,
  CheckBox as TickedIcon, CheckBoxOutlineBlank as UntickedIcon,
  Lock as LockIcon, FilterAlt as UnmarkedIcon,
  Search as SearchIcon, Close as ClearSearchIcon,
} from '@mui/icons-material';
import API from '../BaseURL';
import swal from '../utils/swal';
import CreditorsWeekDialog from './CreditorsWeekDialog';

// ═══════════════════════════════════════════════════════════════════
//  The collection sheet a teacher fills in.
//
//  EVERY collection the teacher is responsible for, on ONE sheet, for
//  ONE class. A learner registered for feeding AND transport shows both
//  on the same visit — the teacher walks the class once, collects one
//  lump sum, and never flips a dropdown to find the rest of what that
//  learner owes for the day. Flipping was how a second collection got
//  forgotten: the round was walked, the sheet said "done", and the
//  transport column had never been opened.
//
//  A learner's name, number and class are written once and the row
//  block spans however many collections they hold, so the sheet still
//  reads as one line per learner.
//
//  The three statuses are the three things that can happen, in the
//  school's own words:
//
//     PAID       took it, and the money is in hand
//     CREDIT     took it, money not received — it becomes arrears
//     NOT EATEN  did not take it — nothing is owed
//
//  Typing less than the full fee makes it a part payment on its own;
//  the shortfall is owed exactly as a credit is.
//
//  Nothing here reaches whoever approves until "Submit for Approval" is
//  pressed. Every keystroke is still saved as it happens — see the
//  DRAFT state in DailyAttendanceService — so a flat battery loses
//  nothing, but a half-walked round is never approved underneath the
//  teacher still walking it.
// ═══════════════════════════════════════════════════════════════════

const TNUM = { fontVariantNumeric: 'tabular-nums' };
const money = (n) => Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const AUTOSAVE_MS = 700;

// What the backend calls each of the four buttons.
//   PAID      → full, or partial when less than the fee is typed
//   PART PAID → 'partial': some of it now, the rest owed
//   CREDIT    → 'none': came, took it, did not pay (the rule turns that
//               into arrears)
//   NOT EATEN → 'absent': nothing taken, so nothing charged
const STATUS_OF = { paid: 'full', part: 'partial', credit: 'none', noteaten: 'absent' };

// The remark writes ITSELF from the status, for every status but one.
// A teacher walking fifty learners will not stop to choose "Paid" from
// a list after pressing PAID — they would leave the column blank, and a
// blank column is what makes a day impossible to answer for afterwards.
const AUTO_REMARK = { full: 'Paid', partial: 'Part payment', none: 'Payment pending' };

// NOT EATEN is the one the system cannot answer for itself: nothing was
// taken, and only the teacher standing there knows why. It defaults to
// the usual answer so the common case still costs no keystrokes, and
// the list is fixed so a day can be counted afterwards — "six absent,
// two sick". Free text spelled six ways answers nothing.
const NOT_EATEN_DEFAULT = 'Absent';
const NOT_EATEN_REASONS = ['Absent', 'Sick', 'Did not eat', 'Forgot money',
                           'Went home early', 'Brought own food', 'Other'];

// ── How far a line has travelled, and what that permits ───────────
//
// Handing a class in does NOT close it. A learner marked absent who
// turns up late, pays, and eats has to be corrected, and the correction
// has to work after the round was submitted — that is the ordinary
// shape of a school morning. Editing a submitted line simply pulls it
// back to DRAFT; the teacher hands the class in again and the approver
// sees the corrected figure.
//
// What DOES close a line is somebody else having ruled on it. Once an
// approver has taken the money (APPROVED), refused it (REJECTED) or
// backed it out (REVERSED), that is their decision and a teacher cannot
// edit it away from underneath — it needs the formal route. The backend
// enforces exactly this list in DailyAttendanceService.recordAttendance;
// this is only so the teacher is told before they tap, rather than after.
const LOCKED_STATES = ['APPROVED', 'REJECTED', 'REVERSED'];
const lockStateOf = (e) => {
  const s = String(e?.approval_status || '').toUpperCase();
  return LOCKED_STATES.includes(s) ? s : null;
};
const isSubmitted = (e) => String(e?.approval_status || '').toUpperCase() === 'PENDING';

const LOCK_TONE = {
  APPROVED: { bg: '#cbeeda', fg: '#15803d', line: '#2e9e4f', word: 'Approved — locked' },
  REJECTED: { bg: '#fbd2d2', fg: '#b91c1c', line: '#e13b3b', word: 'Rejected — locked' },
  REVERSED: { bg: '#e2e8f0', fg: '#475569', line: '#94a3b8', word: 'Reversed — locked' },
};

const prettyType = (t) => String(t || '')
  .replace(/_+$/, '').replace(/_/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

// What is still to be collected on a line once money paid ahead is
// spent. A line already settled from credit counts that credit as held
// again, because re-marking it hands the credit back before the new mark
// is worked out (DailyAttendanceService.recordAttendance).
const dueAfterCredit = (e) => {
  const fee  = Number(e?.daily_amount || 0);
  const held = Number(e?.credit || 0) + (e?.attendance_id ? Number(e?.credit_used || 0) : 0);
  return Math.max(0, fee - held);
};

// One key per (learner × collection) — the same learner now has a cell
// on more than one line, so the learner's id alone no longer identifies
// what is being typed into.
const cellKey = (studentId, type) => `${studentId}|${type}`;

const isoToday = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const longDate = (iso) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || '')) return iso || '';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
};

// A different tint per collection, so the second and third lines of a
// learner's block are told apart at a glance without reading them.
const TYPE_TINTS = [
  { bg: '#e8f1fb', fg: '#1c5b96', line: '#c6dcf1' },
  { bg: '#f1ebfa', fg: '#6b3fa0', line: '#ddd0f0' },
  { bg: '#e6f5f3', fg: '#0f766e', line: '#c4e6e1' },
  { bg: '#fdeef3', fg: '#a3396a', line: '#f3d2df' },
  { bg: '#fdf2e3', fg: '#9a5b12', line: '#f1ddbe' },
];
const tintFor = (types, t) => TYPE_TINTS[Math.max(0, types.indexOf(t)) % TYPE_TINTS.length];

// ── One of the three status buttons ────────────────────────────────
//
// A radio dot plus a word: the teacher is picking one of three mutually
// exclusive things, and a radio is what that looks like.
//
// Each keeps its own colour whether or not it is chosen — green, amber,
// red — so the eye finds "NOT EATEN" without reading it, and PAID is
// green on every single row rather than turning green only once it has
// been pressed. All three behave the same way:
//
//    unchosen   its own pale tint, its own rule, its own lettering,
//               and a hollow ring in its own colour
//    chosen     the same colour a shade deeper, the ring filled in
//
// So what changes on a press is the DEPTH of the colour, never which
// colour it is. Nothing is greyed back — a washed-out pill on a white
// row reads as disabled, and none of them are.
//
// Fixed width rather than stretching, so the three read as one control
// and the column lines up down the whole sheet.
const PILL = {
  success: { fill: '#eaf7ef', fillOn: '#cbeeda', line: '#b9e3ca', lineOn: '#2e9e4f',
             dot: '#2e9e4f', text: '#15803d' },
  info:    { fill: '#eaf2fb', fillOn: '#cddff3', line: '#c6dcf1', lineOn: '#2f6fb0',
             dot: '#2f6fb0', text: '#1c5b96' },
  warning: { fill: '#fdf4e4', fillOn: '#fbe0b4', line: '#f3ddb8', lineOn: '#e0901c',
             dot: '#ef8f1c', text: '#a1610a' },
  error:   { fill: '#fdeeee', fillOn: '#fbd2d2', line: '#f6cccc', lineOn: '#e13b3b',
             dot: '#e13b3b', text: '#b91c1c' },
};

function StatusPill({ tone, label, selected, disabled, onClick, wide }) {
  const c = PILL[tone];
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      role="radio"
      aria-checked={selected}
      sx={{
        // `wide` is the phone: the pill fills its half of a 2×2 grid and
        // stands 40px tall, which is the smallest thing a thumb hits
        // reliably. 29px was sized for a mouse.
        width: wide ? '100%' : 104,
        height: wide ? 40 : 29,
        px: wide ? 1.2 : 0.9,
        justifyContent: wide ? 'center' : 'flex-start',
        display: 'flex', alignItems: 'center', gap: 0.6,
        border: '1.5px solid', borderRadius: 999,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        userSelect: 'none',
        transition: 'background-color .12s, border-color .12s',
        borderColor: selected ? c.lineOn : c.line,
        bgcolor: selected ? c.fillOn : c.fill,
        '&:hover': disabled ? {} : {
          borderColor: c.lineOn,
          bgcolor: selected ? c.fillOn : alpha(c.dot, 0.16),
        },
      }}>
      <Box sx={{
        width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
        border: '2px solid',
        borderColor: c.dot,
        bgcolor: selected ? c.dot : '#fff',
        boxShadow: selected ? 'inset 0 0 0 2px #fff' : 'none',
      }} />
      <Typography sx={{
        fontSize: wide ? 11.5 : 10, fontWeight: 800, letterSpacing: .2,
        whiteSpace: 'nowrap', color: c.text,
      }}>
        {label}
      </Typography>
    </Box>
  );
}

// ── The three controls, shared by the table and the phone cards ────
//
// Defined at MODULE level, not inside the screen component. A component
// declared inside a render gets a fresh identity every render, so React
// unmounts and remounts it — and the amount box would lose focus on
// every keystroke, which is unusable on a phone keyboard.

function StatusPills({ st, disabled, onPick, wide }) {
  const pills = [
    ['success', 'PAID',      'paid'],
    ['info',    'PART PAID', 'part'],
    ['warning', 'CREDIT',    'credit'],
    ['error',   'NOT EATEN', 'noteaten'],
  ];
  const body = pills.map(([tone, label, which]) => (
    <StatusPill key={which} tone={tone} label={label} wide={wide}
      selected={st === which} disabled={disabled}
      onClick={() => onPick(which)} />
  ));
  // Four pills side by side need 452px. A phone has 360. Two by two.
  return wide
    ? <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.8 }}>{body}</Box>
    : <Stack direction="row" spacing={0.7}>{body}</Stack>;
}

function AmountField({ value, placeholder, disabled, error, onChange, wide }) {
  return (
    <TextField
      size="small" type="number"
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      error={error}
      onChange={onChange}
      inputProps={{ min: 0, step: '0.01', inputMode: 'decimal',
        style: {
          textAlign: 'right',
          // 16px is not a style choice: below it, iOS Safari zooms the
          // whole page in on focus and the teacher has to pinch back out
          // after every learner.
          padding: wide ? '11px 12px' : '6px 7px',
          fontSize: wide ? 16 : 13,
          fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums',
        } }}
      sx={{
        width: wide ? '100%' : 100,
        '& .MuiOutlinedInput-root': { bgcolor: '#fff' },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#94a3b8' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2f6fb0' },
      }} />
  );
}

// The remark follows the status by itself — PAID writes "Paid", PART
// PAID writes "Part payment", CREDIT writes "Payment pending". Nobody
// walking fifty learners will stop to choose "Paid" from a list, and the
// column would simply stay empty.
//
// NOT EATEN is the one the system cannot answer: it opens as a list,
// already on "Absent", so the usual case still costs nothing and "Sick"
// is one tap away.
function RemarkField({ st, value, disabled, onChange, wide }) {
  if (!st) return <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>–</Typography>;

  if (st === 'noteaten') {
    return (
      <TextField select size="small" hiddenLabel fullWidth
        variant={wide ? 'outlined' : 'standard'}
        value={value}
        disabled={disabled}
        onChange={onChange}
        SelectProps={{ native: true, ...(wide ? {} : { disableUnderline: true }) }}
        InputProps={wide ? {} : { disableUnderline: true }}
        sx={{
          '& .MuiInputBase-input': {
            py: wide ? 1.1 : 0.2,
            fontSize: wide ? 15 : 12,
            fontWeight: 700, color: '#b91c1c',
            bgcolor: wide ? '#fff' : 'transparent', cursor: 'pointer',
          },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#f6cccc' },
        }}>
        {NOT_EATEN_REASONS.map(x => <option key={x} value={x}>{x}</option>)}
        {value && !NOT_EATEN_REASONS.includes(value) && <option value={value}>{value}</option>}
      </TextField>
    );
  }

  return (
    <Typography sx={{
      fontSize: wide ? 13 : 12, fontWeight: 700,
      color: st === 'paid' ? '#15803d' : st === 'part' ? '#1c5b96' : '#a1610a',
    }}>
      {value}
    </Typography>
  );
}

// A line an approver has already ruled on. The figures stay readable —
// the teacher still needs to see what was recorded — but nothing on the
// line can be changed from here.
function LockedChip({ state, st, wide }) {
  const c = LOCK_TONE[state] || LOCK_TONE.REVERSED;
  const said = st === 'paid' ? 'Paid' : st === 'part' ? 'Part paid'
             : st === 'credit' ? 'Credit' : st === 'noteaten' ? 'Not eaten' : '';
  return (
    <Chip size="small" icon={<LockIcon sx={{ fontSize: 14, color: `${c.fg} !important` }} />}
      label={said ? `${said} · ${c.word}` : c.word}
      sx={{
        fontWeight: 800, fontSize: wide ? 12 : 10.5,
        height: wide ? 34 : 24, width: wide ? '100%' : 'auto',
        bgcolor: c.bg, color: c.fg, border: `1px solid ${c.line}`,
      }} />
  );
}

// Handed in, not yet ruled on. Still fully editable — this only says
// where it is, so a teacher correcting a late arrival knows the change
// has to be handed in again.
function SubmittedChip({ wide }) {
  return (
    <Chip size="small"
      label="Submitted — editing sends it back for approval"
      sx={{
        fontWeight: 700, fontSize: wide ? 10.5 : 9.5,
        height: wide ? 24 : 20, mt: 0.5,
        bgcolor: '#eaf2fb', color: '#1c5b96', border: '1px solid #c6dcf1',
      }} />
  );
}

// "Paid ahead" — settled from money already on account. Nothing for the
// teacher to do, and nothing to collect.
function PaidAheadChip({ amount, wide }) {
  return (
    <Chip size="small" label={`Paid ahead · ${money(amount)} from credit`}
      sx={{
        fontWeight: 800, fontSize: wide ? 12 : 10.5,
        height: wide ? 34 : 24, width: wide ? '100%' : 'auto',
        bgcolor: '#cbeeda', color: '#15803d', border: '1px solid #2e9e4f',
      }} />
  );
}

// ── A figure in the summary strip ──────────────────────────────────
// Label above, figure below, thin rule between each — the shape the
// summary strip has on the sheet this replaces.
const SUM_TONE = { success: '#16a34a', info: '#2f6fb0', warning: '#d97706', error: '#dc2626' };

function SumCell({ label, value, tone, divider = true }) {
  return (
    <Box sx={{
      px: 2.2, py: 0.3, textAlign: 'center', flex: '1 1 auto', minWidth: 92,
      borderRight: divider ? '1px solid #cfe7d6' : 0,
    }}>
      <Typography sx={{
        fontSize: 10.5, fontWeight: 600, color: '#64748b',
        whiteSpace: 'nowrap', lineHeight: 1.6,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: 17, fontWeight: 800, ...TNUM, lineHeight: 1.25,
        color: tone ? SUM_TONE[tone] : '#0f172a',
      }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function TeacherCollectionSheet() {
  // Below `md` the sheet becomes a list of cards. That is the layout the
  // round is actually walked in — a phone, one learner at a time.
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('md'));

  const [date, setDate]   = useState(isoToday);
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [className, setClassName]   = useState('');
  // Which collections are ticked onto the sheet. `null` means "all of
  // them", which is what the sheet is for — a teacher should never have
  // to tick anything to see the whole of a learner's day.
  const [chosen, setChosen] = useState(null);
  const [onlyCreditors, setOnlyCreditors] = useState(false);
  // Find one learner in a class of sixty without scrolling for them.
  // A collector is usually standing in front of the child they need,
  // so the fastest route to that row is typing part of the name.
  const [search, setSearch] = useState('');
  // The Creditors button opens the week, rather than silently filtering
  // the sheet. `pinned` is the list the dialog can hand back, so
  // "show only these" still works from inside it.
  const [creditorsOpen, setCreditorsOpen] = useState(false);
  // A pinned list narrows the sheet to a named handful of learners.
  // `pinnedWhy` is what the banner says they are, so "show everyone"
  // is never a mystery.
  const [pinned, setPinned] = useState(null);
  const [pinnedWhy, setPinnedWhy] = useState('');
  const [drafts, setDrafts] = useState({ drafts: 0, total: 0, dayDrafts: 0, dayTotal: 0 });
  const [submitting, setSubmitting] = useState(false);

  // Per (learner × collection): what is typed, whether it is saving,
  // any error.
  const [cell, setCell] = useState({});
  const timers = useRef({});

  // ── Load ────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await API.get('/collections/me/day-sheet', { params: { date } });
      setSheet(r.data || null);
      setCell({});
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not load the sheet');
      setSheet(null);
    } finally { setLoading(false); }
  }, [date]);

  // Scoped to the class on screen: the button must count what pressing
  // it will actually hand in. `day_*` is the whole day, for the note
  // that says what is still in the teacher's hands overall.
  const refreshDrafts = useCallback(async () => {
    try {
      const r = await API.get('/collections/me/drafts',
        { params: { date, ...(className ? { class_name: className } : {}) } });
      setDrafts({
        drafts:     Number(r.data?.drafts || 0),
        total:      Number(r.data?.total || 0),
        dayDrafts:  Number(r.data?.day_drafts ?? r.data?.drafts ?? 0),
        dayTotal:   Number(r.data?.day_total  ?? r.data?.total  ?? 0),
      });
    } catch { /* the button simply shows nothing to submit */ }
  }, [date, className]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { refreshDrafts(); }, [refreshDrafts]);

  const allTypes = useMemo(() => sheet?.income_types || [], [sheet]);

  // A tick means "on the sheet". Untick to concentrate on one column;
  // the last tick cannot be removed, because a sheet with no collection
  // on it is just an empty table.
  const isTicked = useCallback(
    (t) => !chosen || chosen.includes(t), [chosen]);

  const toggleType = (t) => setChosen((prev) => {
    const cur = prev || allTypes;
    const next = allTypes.filter(x => (x === t ? !cur.includes(x) : cur.includes(x)));
    if (!next.length) return prev;                 // never untick them all
    return next.length === allTypes.length ? null : next;
  });

  const types = useMemo(
    () => allTypes.filter(isTicked),
    [allTypes, isTicked]
  );

  // A day that changes underneath the selection must not leave a stale
  // type ticked on.
  useEffect(() => {
    setChosen(prev => (prev && prev.some(t => !allTypes.includes(t)) ? null : prev));
  }, [allTypes]);

  const sessionOf = useCallback(
    (t) => sheet?.sessions?.[t] || null, [sheet]);
  const isOpen = useCallback(
    (t) => sessionOf(t)?.status === 'open', [sessionOf]);

  const closedTypes = useMemo(() => types.filter(t => !isOpen(t)), [types, isOpen]);

  // ── Learners, each carrying every collection they hold ───────────
  const allGroups = useMemo(() => {
    const out = [];
    for (const s of sheet?.students || []) {
      // Only the collections this learner is actually registered for,
      // in the sheet's own order so the columns line up down the page.
      const entries = types
        .filter(t => s.types?.[t])
        .map(t => ({ income_type: t, entry: s.types[t] }));
      if (!entries.length) continue;
      out.push({
        student_id: s.student_id,
        name: s.student_name || s.student_id,
        class_name: s.class_name || '',
        entries,
      });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [sheet, types]);

  // The class list and the cards at the top are read from the RAW sheet,
  // never from the ticked-down view — unticking a collection must not
  // make a class disappear from the dropdown, nor a card read zero
  // students at the very moment it is asking to be ticked back on.
  const classes = useMemo(
    () => [...new Set((sheet?.students || [])
      .map(s => s.class_name).filter(Boolean))].sort(),
    [sheet]
  );

  const typeInfo = useMemo(() => {
    const info = {};
    for (const t of allTypes) info[t] = { fee: 0, students: 0 };
    for (const s of sheet?.students || []) {
      if (className && (s.class_name || '') !== className) continue;
      for (const t of allTypes) {
        const e = s.types?.[t];
        if (!e) continue;
        info[t].students += 1;
        if (!info[t].fee) info[t].fee = Number(e.daily_amount || 0);
      }
    }
    return info;
  }, [sheet, allTypes, className]);

  useEffect(() => {
    if (classes.length && !classes.includes(className)) setClassName(classes[0]);
  }, [classes, className]);

  // ── Who has not been touched at all ─────────────────────────────
  //
  // Read over the WHOLE day, across every class and every collection —
  // never the filtered view. Unticking a collection must not make an
  // unmarked learner vanish from the count.
  //
  // What it GATES, though, is one class at a time. A collector holding
  // four classes walks them one at a time; a class that has been fully
  // marked is finished, and holding its money back until Nursery 4 is
  // also done is how a teacher ends up carrying three classes' cash
  // around all afternoon. So the submit is scoped to the class on
  // screen, and the rest is reported rather than enforced.
  //
  // "Not touched" means no attendance row and nothing typed. Every
  // learner has to be answered for: they paid, they part-paid, they
  // took it on credit, or they did not take it. There is no fifth
  // thing, so a blank is always an omission.
  const unmarkedAll = useMemo(() => {
    const out = [];
    for (const s of sheet?.students || []) {
      for (const t of allTypes) {
        const e = s.types?.[t];
        if (!e) continue;
        if (e.attendance_id) continue;
        if (cell[cellKey(s.student_id, t)]?.status) continue;
        out.push({
          student_id: s.student_id,
          name: s.student_name || s.student_id,
          class_name: s.class_name || '',
          income_type: t,
        });
      }
    }
    return out;
  }, [sheet, allTypes, cell]);

  // Broken down by class, so the teacher is told where to go back to.
  const unmarkedByClass = useMemo(() => {
    const m = new Map();
    for (const u of unmarkedAll) {
      const c = u.class_name || '—';
      if (!m.has(c)) m.set(c, { entries: 0, students: new Set() });
      m.get(c).entries += 1;
      m.get(c).students.add(u.student_id);
    }
    return [...m.entries()]
      .map(([cls, v]) => ({ cls, entries: v.entries, students: v.students.size }))
      .sort((a, b) => a.cls.localeCompare(b.cls));
  }, [unmarkedAll]);

  // What blocks THIS submission, and what is merely still outstanding
  // somewhere else.
  const unmarkedHere = useMemo(
    () => unmarkedAll.filter(u => !className || u.class_name === className),
    [unmarkedAll, className]
  );
  const otherClassesLeft = useMemo(
    () => unmarkedByClass.filter(u => u.cls !== className),
    [unmarkedByClass, className]
  );

  // Some schools would rather bank what was collected than hold a whole
  // class's cash because one child was at the clinic. When the school
  // has turned the requirement off, an unmarked learner stops BLOCKING
  // the hand-in — but it is still reported, because a teacher should
  // know what they are leaving behind, and the confirmation says it
  // again before anything is sent.
  const requireAll = sheet?.settings?.require_all_marked !== 0;

  const classMarked   = unmarkedHere.length === 0;
  const classComplete = classMarked || !requireAll;
  const dayComplete   = unmarkedAll.length === 0;

  // ── "Show me the ones I have not done" ──────────────────────────
  //
  // A teacher whose Submit will not light up needs to find the learners
  // holding it back, and on a class of fifty that is a scroll looking
  // for blanks. One press narrows the sheet to exactly those.
  //
  // The list is SNAPSHOT at the moment it is pressed rather than
  // recomputed as they work. If it re-filtered live, each learner would
  // vanish the instant they were marked — the teacher would lose their
  // place, and would never see what they had just done. Frozen, the
  // rows stay put and fill in one by one.
  const unmarkedNames = useMemo(
    () => [...new Set(unmarkedHere.map(u => u.student_id))], [unmarkedHere]);

  const showOnlyUnmarked = () => {
    if (!unmarkedNames.length) return;
    setPinned(unmarkedNames);
    setPinnedWhy('still to be marked');
    // Any other narrowing has to come off, or the teacher presses
    // "show me the 8 left" and sees three, because a search from ten
    // minutes ago is still filtering underneath.
    setOnlyCreditors(false);
    setSearch('');
  };

  // Owing on ANY of their collections keeps the whole learner on the
  // sheet — chasing an arrear is chasing the learner, not the column.
  //
  // The search matches the NAME or the INDEX NUMBER, because a teacher
  // reads a name off a face and an index number off a card, and which
  // one they have to hand is not something the screen gets to choose.
  // Matched loosely — spacing and case ignored — so "kwame o" finds
  // "KWAME  OSEI" and an index number with a stray space still lands.
  const groups = useMemo(() => {
    const q = search.trim().toLowerCase().replace(/\s+/g, ' ');
    const hit = (g) => {
      if (!q) return true;
      const name = String(g.name || '').toLowerCase().replace(/\s+/g, ' ');
      const id   = String(g.student_id || '').toLowerCase().replace(/\s+/g, '');
      return name.includes(q) || id.includes(q.replace(/\s+/g, ''));
    };
    return allGroups.filter(g =>
      (!className || g.class_name === className) &&
      (!onlyCreditors || g.entries.some(x => Number(x.entry.arrears || 0) > 0)) &&
      (!pinned || pinned.includes(g.student_id)) &&
      hit(g)
    );
  }, [allGroups, className, onlyCreditors, pinned, search]);

  // How many the search is hiding. A teacher who has typed a name and
  // forgotten it is there would otherwise believe the class is empty.
  const searchHiding = useMemo(() => {
    if (!search.trim()) return 0;
    const inClass = allGroups.filter(g =>
      (!className || g.class_name === className) &&
      (!onlyCreditors || g.entries.some(x => Number(x.entry.arrears || 0) > 0)) &&
      (!pinned || pinned.includes(g.student_id))
    ).length;
    return Math.max(0, inClass - groups.length);
  }, [allGroups, className, onlyCreditors, pinned, groups.length, search]);

  // ── Saving ──────────────────────────────────────────────────────
  const commit = useCallback(async (g, income_type, e, { amount, status, note }) => {
    const sid = e.session_id || sheet?.sessions?.[income_type]?.id;
    if (!sid) return;
    const k = cellKey(g.student_id, income_type);

    const amt = Number(amount ?? 0) || 0;
    // Measured against what is still DUE after credit, not the bare fee.
    // A learner whose day is covered by money paid ahead and who hands
    // over GH¢ 5 more is topping up their credit, not part-paying — the
    // backend turns cash beyond the day's charge into credit on approval.
    const due = dueAfterCredit(e);
    // The button says what was meant; the amount typed says what
    // actually happened, and it wins. PAID with less than is due is a
    // part payment; PART PAID with the whole of it is simply paid.
    let resolved = status;
    if (status === 'full'    && amt > 0 && amt < due) resolved = 'partial';
    if (status === 'partial' && amt >= due && (due > 0 || amt > 0)) resolved = 'full';
    // A part payment of nothing is not a part payment — it is a learner
    // who took it and paid nothing, which is CREDIT. The backend rejects
    // partial-with-zero outright, and rightly; this says what was meant
    // instead of handing the teacher a validation error.
    if (resolved === 'partial' && amt <= 0) resolved = 'none';

    // ── The remark writes itself ──────────────────────────────────
    // Three of the four statuses say their own remark, so the teacher
    // never types one. NOT EATEN is the exception: the system cannot
    // know WHY nothing was taken, so the teacher's choice stands, and
    // "Absent" is assumed until they say otherwise.
    const notes = resolved === 'absent'
      ? (note ?? cell[k]?.note ?? e.notes ?? NOT_EATEN_DEFAULT)
      : (AUTO_REMARK[resolved] || '');

    setCell(p => ({ ...p, [k]: { ...(p[k] || {}), saving: true, error: null, note: notes } }));
    try {
      const r = await API.post(`/daily-collections/sessions/${sid}/attendance`, {
        student_id: g.student_id,
        status: resolved,
        amount_paid: resolved === 'absent' || resolved === 'none' ? 0 : amt,
        notes,
      });
      const saved = r?.data || {};
      // Fold the saved row back in so the balances and the summary move
      // without a reload.
      setSheet(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          students: prev.students.map(s => (
            s.student_id !== g.student_id ? s : {
              ...s,
              types: {
                ...s.types,
                [income_type]: {
                  ...s.types[income_type],
                  attendance_id: saved.attendance_id ?? s.types[income_type].attendance_id ?? 1,
                  status:        saved.status        ?? resolved,
                  amount_paid:   saved.amount_paid   ?? (resolved === 'absent' || resolved === 'none' ? 0 : amt),
                  credit_used:   saved.credit_used   ?? s.types[income_type].credit_used,
                  arrears:       saved.arrears       ?? s.types[income_type].arrears,
                  credit:        saved.credit        ?? s.types[income_type].credit,
                  notes,
                  // A re-mark always raises a FRESH draft — the old
                  // collection row is deleted. So a corrected line that
                  // had been handed in goes back to needing a hand-in,
                  // and must stop reading "Submitted".
                  approval_status: 'DRAFT',
                },
              },
            }
          )),
        };
      });
      setCell(p => ({ ...p, [k]: { ...(p[k] || {}), saving: false, savedAt: Date.now() } }));
      refreshDrafts();
    } catch (err) {
      setCell(p => ({ ...p, [k]: {
        ...(p[k] || {}), saving: false,
        error: err?.response?.data?.message || 'Save failed',
      } }));
    }
  }, [sheet, cell, refreshDrafts]);

  // ── What each line currently reads as ───────────────────────────
  const shownAmount = (g, t, e) => {
    const k = cellKey(g.student_id, t);
    if (cell[k]?.amount !== undefined) return cell[k].amount;
    if (!e.attendance_id) return '';
    if (e.status === 'absent' || e.status === 'none') return '0.00';
    // Settled from credit with no cash: leave the box empty so it reads
    // "Add more" — a 0 sitting in it looks like a field that is done.
    if (e.status === 'full' && Number(e.amount_paid || 0) === 0
        && Number(e.credit_used || 0) > 0) return '';
    return String(e.amount_paid ?? '');
  };

  const shownStatus = (g, t, e) => {
    const k = cellKey(g.student_id, t);
    if (cell[k]?.status) return cell[k].status;
    if (!e.attendance_id) return null;
    if (e.status === 'absent')  return 'noteaten';
    if (e.status === 'none')    return 'credit';
    if (e.status === 'partial') return 'part';
    return 'paid';
  };

  // What the Remarks column reads. It follows the status by itself,
  // except on NOT EATEN where the teacher's reason is the answer.
  const shownRemark = (g, t, e) => {
    const st = shownStatus(g, t, e);
    if (!st) return '';
    if (st === 'noteaten') {
      const k = cellKey(g.student_id, t);
      return cell[k]?.note ?? e.notes ?? NOT_EATEN_DEFAULT;
    }
    return AUTO_REMARK[STATUS_OF[st]] || '';
  };

  const pick = (g, t, e, which) => {
    const k = cellKey(g.student_id, t);
    const fee = Number(e.daily_amount || 0);
    // PART PAID opens on half the fee, which is what "part" almost
    // always means on a round — the teacher overtypes it when it isn't.
    // PAID asks only for what money already paid ahead does not cover —
    // the whole fee again would take the parent's money twice. A line
    // already settled from credit gets it back before it is re-marked.
    const due = dueAfterCredit(e);
    const amount = which === 'paid' ? String(due.toFixed(2))
                 : which === 'part' ? String((fee / 2).toFixed(2))
                 : '0.00';
    const note = which === 'noteaten'
      ? (cell[k]?.note ?? e.notes ?? NOT_EATEN_DEFAULT)
      : undefined;
    setCell(p => ({ ...p, [k]: { ...(p[k] || {}), status: which, amount, error: null } }));
    clearTimeout(timers.current[k]);
    commit(g, t, e, { amount, status: STATUS_OF[which], note });
  };

  const typeAmount = (g, t, e, value) => {
    const k = cellKey(g.student_id, t);
    const due = dueAfterCredit(e);
    const amt = Number(value) || 0;
    // The pill follows the figure as it is typed, so the sheet says
    // "PART PAID" the moment less than is due is in the box. On a line
    // already covered by credit nothing is due, so any figure typed is
    // extra money paid ahead and the line stays PAID.
    const which = due > 0 && amt > 0 && amt < due ? 'part' : 'paid';
    setCell(p => ({ ...p, [k]: { ...(p[k] || {}), status: which, amount: value, error: null } }));
    clearTimeout(timers.current[k]);
    timers.current[k] = setTimeout(
      () => commit(g, t, e, { amount: value, status: STATUS_OF[which] }), AUTOSAVE_MS);
  };

  // Only ever called on a NOT EATEN line — the other three write their
  // own remark and offer nothing to change.
  const setRemark = (g, t, e, value) => {
    const k = cellKey(g.student_id, t);
    setCell(p => ({ ...p, [k]: { ...(p[k] || {}), note: value } }));
    commit(g, t, e, {
      amount: '0.00',
      status: 'absent',
      note: value,
    });
  };

  // Already settled out of money paid in advance. Left alone, that is the
  // answer for the day and the line counts as marked — the system carries
  // it. But it is NOT locked: the learner may not eat today (NOT EATEN
  // hands the credit back), or the parent may send more money (typed in
  // the box, it tops the credit up once approved).
  const paidAheadOn = (e) => !!e.attendance_id && e.status === 'full'
    && Number(e.amount_paid || 0) === 0 && Number(e.credit_used || 0) > 0;

  // Mark a learner's whole block in one press — the common case when a
  // learner hands over the full amount for everything they are on.
  const pickWholeRow = (g, which) => {
    for (const x of g.entries) {
      if (!isOpen(x.income_type)) continue;
      if (paidAheadOn(x.entry)) continue;
      // A line an approver has ruled on is not ours to sweep over.
      if (lockStateOf(x.entry)) continue;
      pick(g, x.income_type, x.entry, which);
    }
  };

  // ── The summary strip, and the same figures per collection ──────
  const { sum, perType } = useMemo(() => {
    const blank = () => ({ entries: 0, paid: 0, part: 0, credit: 0, notEaten: 0, unmarked: 0,
                           amountPaid: 0, amountCredit: 0, notEatenValue: 0, totalValue: 0 });
    const z = blank();
    z.students = groups.length;
    const per = {};
    for (const t of types) per[t] = blank();

    for (const g of groups) {
      for (const { income_type: t, entry: e } of g.entries) {
        const p = per[t] || (per[t] = blank());
        const fee = Number(e.daily_amount || 0);
        z.entries += 1;      p.entries += 1;
        z.totalValue += fee; p.totalValue += fee;

        const st = shownStatus(g, t, e);
        if (!st) { z.unmarked += 1; p.unmarked += 1; continue; }

        if (st === 'paid' || st === 'part') {
          const a = Number(shownAmount(g, t, e)) || 0;
          if (st === 'part') { z.part += 1; p.part += 1; }
          else               { z.paid += 1; p.paid += 1; }
          z.amountPaid += a;  p.amountPaid += a;
          // Anything short of the fee is owed, exactly as a credit is.
          const short = fee - a - Number(e.credit_used || 0);
          if (short > 0.009) { z.amountCredit += short; p.amountCredit += short; }
        } else if (st === 'credit') {
          z.credit += 1;           p.credit += 1;
          z.amountCredit += fee;   p.amountCredit += fee;
        } else {
          z.notEaten += 1;         p.notEaten += 1;
          z.notEatenValue += fee;  p.notEatenValue += fee;
        }
      }
    }
    return { sum: z, perType: per };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, types, cell]);


  // ── Actions ─────────────────────────────────────────────────────
  const openDay = async () => {
    try {
      await API.post('/collections/me/open-day', { date, is_school_day: true });
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Could not open the day')); }
  };

  const submit = async () => {
    if (!drafts.drafts || submitting) return;
    // The button is already disabled; this is the second lock, so a
    // stale click or a keyboard activation cannot get past it either.
    // It gates on THIS class only — the other classes are the teacher's
    // remaining work, not a reason to hold this class's money.
    if (!classComplete) {
      const names = [...new Set(unmarkedHere.map(u => u.name))].slice(0, 8)
        .map(n => `<li>${n}</li>`).join('');
      const more = new Set(unmarkedHere.map(u => u.name)).size - 8;
      await swal.alertHtml({
        title: `${className || 'This class'} is not finished`,
        icon: 'warning',
        html: `<b>${unmarkedHere.length}</b> entr`
            + `${unmarkedHere.length === 1 ? 'y has' : 'ies have'} not been touched:`
            + `<ul style="text-align:left;display:inline-block;margin-top:.6em">${names}</ul>`
            + (more > 0 ? `<br/>…and ${more} more student(s)` : '')
            + '<br/><br/>Every student has to be answered for — paid, part paid, '
            + 'credit, or not eaten.',
      });
      return;
    }
    const ok = await swal.confirm({
      title: className ? `Submit ${className} for approval?` : 'Submit for approval?',
      tone: unmarkedHere.length ? 'warning' : 'success',
      html: `<b>${drafts.drafts}</b> entr${drafts.drafts === 1 ? 'y' : 'ies'} `
          + `&middot; <b>GH¢ ${money(drafts.total)}</b>`
          + (className ? `<br/><span style="opacity:.75">${className} only</span>` : '')
          // Allowed, but never silent. A teacher handing in a part-walked
          // round is told exactly how many learners they are leaving out.
          + (unmarkedHere.length
              ? `<br/><br/><b style="color:#a16207">${unmarkedHere.length} `
                + `student${unmarkedHere.length === 1 ? '' : 's'} in this class `
                + `${unmarkedHere.length === 1 ? 'has' : 'have'} not been marked</b> `
                + 'and will NOT be part of this hand-in. You can mark them later '
                + 'and submit again.'
              : '')
          + '<br/><br/>Whoever approves can only see what you submit. '
          + 'You can still correct a student afterwards and hand it in again — '
          + 'a line only locks once it has been approved. '
          + (otherClassesLeft.length
              ? `Your other ${otherClassesLeft.length === 1 ? 'class' : 'classes'} `
                + `(${otherClassesLeft.map(u => u.cls).join(', ')}) `
                + 'stay with you until you finish them.'
              : 'You can carry on marking afterwards.'),
      confirmText: 'Yes, submit',
    });
    if (!ok) return;
    setSubmitting(true);
    try {
      const r = await API.post('/collections/me/submit-day',
        { date, ...(className ? { class_name: className } : {}) });
      swal.success(r.data?.message || 'Submitted for approval');
      await Promise.all([load(), refreshDrafts()]);
    } catch (e) { swal.error(swal.pickError(e, 'Could not submit')); }
    finally { setSubmitting(false); }
  };

  const creditorCount = useMemo(
    () => allGroups.filter(g => g.entries.some(x => Number(x.entry.arrears || 0) > 0)).length,
    [allGroups]
  );

  // ── Render ──────────────────────────────────────────────────────
  if (loading) {
    return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 1360, mx: 'auto', p: { xs: 0.9, sm: 2 } }}>

      {/* ── Header ──────────────────────────────────────────────── */}
      <Card elevation={0} sx={{
        p: { xs: 1.3, md: 2.1 }, mb: 1.2, borderRadius: 2.5,
        bgcolor: '#fff', border: '1px solid #e6ecf4',
      }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.2, md: 2 }}
          alignItems={{ md: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1.4} alignItems="center">
            <Box sx={{
              width: { xs: 38, md: 50 }, height: { xs: 38, md: 50 },
              borderRadius: '50%', flexShrink: 0,
              bgcolor: '#2f6fb0', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <MealIcon sx={{ fontSize: { xs: 21, md: 26 } }} />
            </Box>
            <Box>
              <Typography sx={{
                fontSize: { xs: 16, md: 21 }, fontWeight: 800,
                color: '#1e3a6b', lineHeight: 1.25,
              }}>
                Daily Collection Sheet
              </Typography>
              {/* The blurb is desk reading. On a phone it is three lines
                  of screen the teacher scrolls past every single time. */}
              <Typography sx={{
                display: { xs: 'none', md: 'block' },
                fontSize: 12, color: '#64748b', lineHeight: 1.5,
              }}>
                Every collection a student is registered for is on this one sheet.
                Enter payment, mark the status, then submit for approval.
              </Typography>
              <Typography sx={{
                display: { xs: 'block', md: 'none' },
                fontSize: 11, color: '#64748b',
              }}>
                {longDate(date)}
              </Typography>
            </Box>
          </Stack>

          {/* Date and class fill the width on a phone — they are the two
              things a teacher changes, and a 130px target beside a 168px
              one leaves neither comfortable to hit. */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField size="small" type="date" label="Date" value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: { xs: 1, md: 'none' }, minWidth: { xs: 140, md: 168 } }}
              InputProps={{ startAdornment: <DateIcon sx={{ fontSize: 17, mr: .7, color: 'text.secondary' }} /> }} />
            <TextField select size="small" label="Class" value={className}
              onChange={(e) => setClassName(e.target.value)}
              sx={{ flex: { xs: 1, md: 'none' }, minWidth: { xs: 120, md: 130 } }}>
              {classes.length === 0 && <MenuItem value="">—</MenuItem>}
              {classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            {/* Widest control on the row, and on a phone it takes the
                whole line. Finding the child in front of you is the
                thing this screen is used for most after marking. */}
            <TextField size="small" placeholder="Search name or ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: { xs: '1 0 100%', md: 1 }, minWidth: { md: 200 } }}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ fontSize: 18, mr: .7, color: 'text.secondary' }} />
                ),
                endAdornment: search ? (
                  <Tooltip title="Clear">
                    <IconButton size="small" onClick={() => setSearch('')} sx={{ p: .3 }}>
                      <ClearSearchIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                ) : null,
              }} />
            <Tooltip title="Reload">
              <span>
                <Button size="small" onClick={load} sx={{ minWidth: 0, px: 1 }}>
                  <RefreshIcon fontSize="small" />
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      {/* A pinned list is easy to forget you are inside, and a teacher
          who forgets will think half their class has vanished. So it
          says so, and the way out is on the same line. */}
      {pinned && (
        <Alert severity="info" sx={{ mb: 1.5, py: 0.3 }}
          action={
            <Button size="small" onClick={() => { setPinned(null); setPinnedWhy(''); }}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Show everyone
            </Button>
          }>
          Showing only the {pinned.length} learner{pinned.length === 1 ? '' : 's'}
          {' '}{pinnedWhy || 'you picked'}.
          {/* Marked ones stay on screen so the teacher can see what
              they have just done, and the count says what is left. */}
          {pinnedWhy === 'still to be marked' && unmarkedHere.length > 0 && (
            <Box component="span" sx={{ fontWeight: 700 }}>
              {' '}{unmarkedHere.length} still to go.
            </Box>
          )}
          {pinnedWhy === 'still to be marked' && unmarkedHere.length === 0 && (
            <Box component="span" sx={{ fontWeight: 700, color: '#15803d' }}>
              {' '}All done — you can submit now.
            </Box>
          )}
        </Alert>
      )}

      {/* Same reasoning as the pinned banner: a search left in the box
          hides most of the class, and a teacher who has forgotten it is
          there will think the rest have disappeared — or worse, hand the
          class in believing they have marked everyone. */}
      {search.trim() && (
        <Alert severity={groups.length ? 'info' : 'warning'} sx={{ mb: 1.5, py: 0.3 }}
          action={
            <Button size="small" onClick={() => setSearch('')}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Show everyone
            </Button>
          }>
          {groups.length
            ? `Searching "${search.trim()}" — ${groups.length} learner`
              + `${groups.length === 1 ? '' : 's'} shown`
              + (searchHiding ? `, ${searchHiding} hidden.` : '.')
            : `Nobody in ${className || 'this class'} matches "${search.trim()}".`}
        </Alert>
      )}

      {/* ── One card per collection, ticked on ──────────────────────
           Every collection starts ticked, because the whole point of
           the sheet is that a learner's day arrives complete. The ticks
           are there only for a teacher who wants to concentrate on one
           column, and the last one cannot be untargeted. */}
      {/* One compact line each. These used to be big tiles, which on a
          phone pushed the totals below the fold — and the totals are the
          thing a collector checks every few minutes. */}
      <Stack direction="row" spacing={0.8} sx={{ mb: 1.2 }} flexWrap="wrap" useFlexGap>
        {allTypes.map((t) => {
          const on = isTicked(t);
          const tint = tintFor(allTypes, t);
          // Fees can differ per learner; the chip shows the one this
          // collection is actually billed at for the class in view.
          const fee   = typeInfo[t]?.fee || 0;
          const count = typeInfo[t]?.students || 0;
          return (
            <Card key={t} elevation={0}
              onClick={() => toggleType(t)}
              role="checkbox" aria-checked={on}
              sx={{
                px: 1.1, py: 0.6, borderRadius: 2, cursor: 'pointer',
                bgcolor: on ? tint.bg : '#f8fafc',
                border: `${on ? 1.5 : 1}px solid ${on ? tint.fg : '#e2e8f0'}`,
                transition: 'background-color .12s, border-color .12s',
                '&:hover': { borderColor: tint.fg },
              }}>
              <Stack direction="row" spacing={0.7} alignItems="center">
                {on
                  ? <TickedIcon sx={{ color: tint.fg, fontSize: 17 }} />
                  : <UntickedIcon sx={{ color: '#94a3b8', fontSize: 17 }} />}
                <Typography noWrap sx={{
                  fontSize: 11, fontWeight: 800,
                  color: on ? tint.fg : '#94a3b8',
                }}>
                  {prettyType(t)}
                </Typography>
                <Typography sx={{
                  fontSize: 12, fontWeight: 800, ...TNUM,
                  color: on ? '#0f172a' : '#94a3b8',
                }}>
                  GH¢ {money(fee)}
                </Typography>
                <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#64748b', ...TNUM }}>
                  · {count}
                </Typography>
              </Stack>
            </Card>
          );
        })}
      </Stack>

      {/* ── A collection has to be opened before it can be marked ─── */}
      {closedTypes.length > 0 && (
        <Alert severity="info" sx={{ mb: 1.5 }}
          action={<Button size="small" variant="contained" onClick={openDay}>Open the day</Button>}>
          {closedTypes.map(prettyType).join(', ')}
          {closedTypes.length === 1 ? ' has' : ' have'} not been opened for {longDate(date)} yet.
        </Alert>
      )}

      {/* ── Summary, pinned to the top ──────────────────────────────
           This is the one thing a collector checks over and over: how
           much is in their hand. It stays put and the sheet scrolls
           underneath it, because on a phone anything below the fold may
           as well not exist — and the round is walked on a phone.

           Submit for Approval lives here too, next to the figure it is
           submitting, so nobody scrolls to the foot of fifty rows to
           find it. */}
      <Card elevation={0} sx={{
        position: 'sticky', top: 0, zIndex: 30,
        borderRadius: 2.5, mb: 1.2, p: { xs: 1, md: 1.3 },
        bgcolor: '#f1faf4', border: '1px solid #cfe7d6',
        boxShadow: '0 6px 18px rgba(15,23,42,.10)',
      }}>
        <Stack direction="row" spacing={1.2}
          alignItems="center" justifyContent="space-between">

          {/* The headline figure — the cash actually in hand. */}
          <Stack direction="row" spacing={{ xs: 0.9, md: 1.3 }} alignItems="center"
            sx={{ minWidth: 0 }}>
            <ValueIcon sx={{ fontSize: { xs: 24, md: 30 }, color: '#15803d', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{
                fontSize: 10.5, fontWeight: 800, letterSpacing: .5,
                textTransform: 'uppercase', color: '#15803d', lineHeight: 1.5,
              }}>
                Collected so far
              </Typography>
              <Typography sx={{
                fontSize: { xs: 21, md: 26 }, fontWeight: 800, ...TNUM,
                color: '#15803d', lineHeight: 1.15,
              }}>
                GH¢ {money(sum.amountPaid)}
                <Box component="span" sx={{
                  display: { xs: 'block', md: 'inline' },
                  fontSize: { xs: 10.5, md: 11 }, fontWeight: 600, color: '#64748b',
                  ml: { xs: 0, md: 0.9 },
                }}>
                  of GH¢ {money(sum.totalValue)} · {className || 'all classes'}
                </Box>
              </Typography>
              {/* Handing one class in must not hide what is still in the
                  teacher's hands from the rest of the day. */}
              {drafts.dayDrafts > drafts.drafts && (
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#a16207', ...TNUM }}>
                  GH¢ {money(drafts.dayTotal)} still in hand across the whole day
                </Typography>
              )}
            </Box>
          </Stack>

          {/* State of the round, then the button that acts on it. */}
          <Stack direction="row" spacing={1.2} alignItems="center"
            sx={{ flexShrink: 0, flexWrap: 'wrap' }} useFlexGap>
            {/* The state line speaks for the CLASS on screen, because
                that is what the button beside it acts on. What is left
                elsewhere is on the second line — visible, but not in the
                way. On a phone the button says the same thing, so the
                line is dropped rather than repeated. */}
            <Stack direction="row" spacing={0.7} alignItems="center"
              sx={{ display: { xs: 'none', md: 'flex' } }}>
              <CheckIcon sx={{ fontSize: 19, color: classComplete ? '#22a559' : '#d99b20' }} />
              <Typography sx={{
                fontSize: 11.5, fontWeight: 800, lineHeight: 1.35,
                color: classComplete ? '#15803d' : '#a16207',
              }}>
                {classComplete
                  ? `${className || 'This class'} is complete`
                  : `${unmarkedHere.length} still to mark in ${className || 'this class'}`}
                <Box component="span" sx={{
                  display: 'block', fontSize: 10, fontWeight: 600, color: '#64748b',
                }}>
                  {otherClassesLeft.length
                    ? `${otherClassesLeft.length} other class${otherClassesLeft.length === 1 ? '' : 'es'} still to walk`
                    : dayComplete ? 'every class is done' : ''}
                </Box>
              </Typography>
            </Stack>

            {/* The same shortcut, beside the button it unblocks — the
                panel that also offers it can be a scroll away on a long
                class. */}
            {unmarkedHere.length > 0 && !pinned && (
              <Tooltip title="Narrow the sheet to the students you have not touched">
                <Chip size="small" clickable icon={<UnmarkedIcon sx={{ fontSize: 15 }} />}
                  label={`Show the ${unmarkedHere.length} left`}
                  onClick={showOnlyUnmarked}
                  sx={{
                    height: 30, fontSize: 11, fontWeight: 800,
                    bgcolor: '#fdf4e4', color: '#a16207',
                    border: '1px solid #f3ddb8',
                    '&:hover': { bgcolor: '#f7e3c0' },
                  }} />
              </Tooltip>
            )}

            <Tooltip title={!classComplete
              ? `${unmarkedHere.length} entr${unmarkedHere.length === 1 ? 'y has' : 'ies have'} `
                + `not been marked in ${className || 'this class'} — finish it first`
              : drafts.drafts
                ? `Hands in ${className || 'this day'} only — `
                  + `${drafts.drafts} entr${drafts.drafts === 1 ? 'y' : 'ies'}, GH¢ ${money(drafts.total)}`
                : `${className || 'This day'} has already been handed in`}>
              <span>
                <Button variant="contained" disableElevation
                  disabled={!drafts.drafts || submitting || !classComplete}
                  onClick={submit}
                  startIcon={submitting
                    ? <CircularProgress size={15} color="inherit" />
                    : <SubmitIcon />}
                  sx={{
                    textTransform: 'none', fontWeight: 700,
                    fontSize: { xs: 12.5, md: 13 },
                    px: { xs: 1.6, md: 2.2 }, py: { xs: 1.1, md: 1 },
                    minHeight: 42, borderRadius: 2, whiteSpace: 'nowrap',
                    bgcolor: '#22a559', '&:hover': { bgcolor: '#15803d' },
                  }}>
                  {submitting ? 'Submitting…'
                    : !classComplete ? `${unmarkedHere.length} left`
                    : drafts.drafts ? `Submit${isPhone ? '' : ` ${className || 'day'}`} (${drafts.drafts})`
                    : 'Submitted'}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {/* The counts. On a phone these collapse to one line, so the bar
            never grows tall enough to swallow the sheet under it. */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, mt: 0.9, pt: 0.9,
                   borderTop: '1px dashed #cfe7d6' }}>
          <Stack direction="row" alignItems="stretch" flexWrap="wrap" useFlexGap>
            <SumCell label="Students"         value={sum.students} />
            <SumCell label="Entries"          value={sum.entries} />
            <SumCell label="Paid"             value={sum.paid}      tone="success" />
            <SumCell label="Part Paid"        value={sum.part}      tone="info" />
            <SumCell label="Credit"           value={sum.credit}    tone="warning" />
            <SumCell label="Not Eaten"        value={sum.notEaten}  tone="error" />
            <SumCell label="Amount on Credit" value={`GH¢ ${money(sum.amountCredit)}`}  tone="warning" />
            <SumCell label="Not Eaten Amount" value={`GH¢ ${money(sum.notEatenValue)}`} tone="error" divider={false} />
          </Stack>
        </Box>
        <Box sx={{
          display: { xs: 'block', md: 'none' }, mt: 0.8, pt: 0.8,
          borderTop: '1px dashed #cfe7d6',
        }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', ...TNUM }}>
            <Box component="span" sx={{ color: '#16a34a' }}>{sum.paid} paid</Box> ·{' '}
            <Box component="span" sx={{ color: '#2f6fb0' }}>{sum.part} part</Box> ·{' '}
            <Box component="span" sx={{ color: '#d97706' }}>{sum.credit} credit</Box> ·{' '}
            <Box component="span" sx={{ color: '#dc2626' }}>{sum.notEaten} not eaten</Box>
            {sum.amountCredit > 0 && ` · owed GH¢ ${money(sum.amountCredit)}`}
          </Typography>
          {/* The desk layout says this beside the button; on a phone the
              button has no room for it, so it goes here instead. */}
          <Typography sx={{
            fontSize: 10.5, fontWeight: 700, mt: 0.3,
            color: classComplete ? '#15803d' : '#a16207',
          }}>
            {classComplete
              ? `${className || 'This class'} is complete`
              : `${unmarkedHere.length} still to mark in ${className || 'this class'}`}
            {otherClassesLeft.length > 0 && (
              <Box component="span" sx={{ color: '#64748b', fontWeight: 600 }}>
                {' · '}{otherClassesLeft.length} other class
                {otherClassesLeft.length === 1 ? '' : 'es'} left
              </Box>
            )}
          </Typography>
        </Box>

        {/* The same day split by collection — what has to be banked
            under each heading, which the single total cannot say. */}
        {types.length > 1 && (
          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mt: 0.9 }}>
            {types.map((t) => {
              const p = perType[t] || {};
              const tint = tintFor(allTypes, t);
              return (
                <Box key={t} sx={{
                  px: 1.1, py: 0.5, borderRadius: 2,
                  bgcolor: '#fff', border: `1px solid ${tint.line}`,
                }}>
                  <Stack direction="row" spacing={0.7} alignItems="baseline">
                    <Typography sx={{ fontSize: 10, fontWeight: 800, color: tint.fg }}>
                      {prettyType(t)}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, ...TNUM, color: '#0f172a' }}>
                      GH¢ {money(p.amountPaid)}
                    </Typography>
                    <Typography sx={{ fontSize: 9.5, color: '#64748b', ...TNUM }}>
                      {p.paid || 0}p · {p.part || 0}½ · {p.credit || 0}c · {p.notEaten || 0}n
                      {p.unmarked ? ` · ${p.unmarked}?` : ''}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Card>

      {/* ── The sheet, on a phone ───────────────────────────────────
           A seven-column grid on a 360px screen means pinching and
           sideways scrolling for every learner, with the name scrolled
           off by the time the buttons are in view. So on a phone each
           learner is a card instead: name at the top, then one block per
           collection, each with its own amount box and its own four
           buttons two-by-two. Nothing scrolls sideways and nothing is
           smaller than a thumb. */}
      {isPhone ? (
        <Box sx={{ mb: 1.5 }}>
          {groups.length === 0 && (
            <Card elevation={0} sx={{
              p: 4, textAlign: 'center', borderRadius: 2.5,
              border: '1px solid #e6ecf4', color: '#64748b', fontSize: 13,
            }}>
              {onlyCreditors
                ? 'Nobody in this class is owing.'
                : 'No students are registered for these collections in this class.'}
            </Card>
          )}

          <Stack spacing={1.2}>
            {groups.map((g, gi) => {
              const n = g.entries.length;
              const due  = g.entries.reduce((a, x) => a + Number(x.entry.daily_amount || 0), 0);
              const owes = g.entries.reduce((a, x) => a + Number(x.entry.arrears || 0), 0);
              const done = g.entries.every(x => shownStatus(g, x.income_type, x.entry));

              return (
                <Card key={g.student_id} elevation={0} sx={{
                  borderRadius: 2.5, overflow: 'hidden',
                  // A finished learner gets a green edge, so scrolling
                  // back tells you where you got to without reading.
                  border: '1px solid #e6ecf4',
                  borderLeft: `4px solid ${done ? '#2e9e4f' : '#cbd5e1'}`,
                }}>
                  {/* Who */}
                  <Box sx={{ px: 1.5, py: 1.2, bgcolor: '#f6f9fd', borderBottom: '1px solid #dde6f0' }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Typography sx={{
                        fontSize: 12, fontWeight: 800, color: '#64748b',
                        minWidth: 22, pt: 0.2, ...TNUM,
                      }}>
                        {gi + 1}
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>
                          {g.name}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#64748b', ...TNUM }}>
                          {g.class_name}{g.student_id ? ` · ${g.student_id}` : ''}
                        </Typography>
                        {/* The one figure the teacher asks for. */}
                        <Typography sx={{
                          fontSize: 13, fontWeight: 800, color: '#1c5b96', mt: 0.3, ...TNUM,
                        }}>
                          Due GH¢ {money(due)}
                          {n > 1 && (
                            <Box component="span" sx={{ fontSize: 10.5, fontWeight: 600, color: '#64748b', ml: 0.7 }}>
                              for {n} collections
                            </Box>
                          )}
                        </Typography>
                        {owes > 0 && (
                          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#b91c1c', ...TNUM }}>
                            Owing GH¢ {money(owes)}
                          </Typography>
                        )}
                      </Box>
                      {done && <CheckIcon sx={{ fontSize: 21, color: '#2e9e4f' }} />}
                    </Stack>

                    {n > 1 && (
                      <Stack direction="row" spacing={0.8} sx={{ mt: 1 }}>
                        <Chip size="small" label="All paid" clickable
                          onClick={() => pickWholeRow(g, 'paid')}
                          sx={{
                            flex: 1, height: 30, fontSize: 11.5, fontWeight: 800,
                            bgcolor: '#eaf7ef', color: '#15803d', border: '1px solid #b9e3ca',
                          }} />
                        <Chip size="small" label="All credit" clickable
                          onClick={() => pickWholeRow(g, 'credit')}
                          sx={{
                            flex: 1, height: 30, fontSize: 11.5, fontWeight: 800,
                            bgcolor: '#fdf4e4', color: '#a1610a', border: '1px solid #f3ddb8',
                          }} />
                      </Stack>
                    )}
                  </Box>

                  {/* One block per collection they hold */}
                  {g.entries.map(({ income_type: t, entry: e }, ei) => {
                    const k     = cellKey(g.student_id, t);
                    const c     = cell[k] || {};
                    const st    = shownStatus(g, t, e);
                    const fee   = Number(e.daily_amount || 0);
                    const tint  = tintFor(allTypes, t);
                    const open  = isOpen(t);
                    const ahead = paidAheadOn(e);
                    const lock  = lockStateOf(e);
                    const sent  = isSubmitted(e);
                    const editable = open && !lock;
                    return (
                      <Box key={k} sx={{
                        px: 1.5, py: 1.3,
                        borderTop: ei === 0 ? 0 : '1px dashed #dde6f0',
                      }}>
                        <Stack direction="row" spacing={1} alignItems="center"
                          justifyContent="space-between" sx={{ mb: 1 }}>
                          <Chip size="small" label={prettyType(t)}
                            sx={{
                              height: 24, fontSize: 11.5, fontWeight: 800,
                              bgcolor: tint.bg, color: tint.fg,
                              border: `1px solid ${tint.line}`,
                            }} />
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155', ...TNUM }}>
                            Fee GH¢ {money(fee)}
                          </Typography>
                        </Stack>

                        {lock ? (
                          <Stack spacing={0.8}>
                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#334155', ...TNUM }}>
                              Recorded GH¢ {money(shownAmount(g, t, e) || 0)}
                            </Typography>
                            <LockedChip state={lock} st={st} wide />
                          </Stack>
                        ) : (
                          <>
                            {ahead && (
                              <Box sx={{ mb: 1 }}><PaidAheadChip amount={e.credit_used} wide /></Box>
                            )}
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                              <Typography sx={{
                                fontSize: 11.5, fontWeight: 700, color: '#64748b',
                                whiteSpace: 'nowrap',
                              }}>
                                Amount
                              </Typography>
                              <Box sx={{ flex: 1 }}>
                                <AmountField wide
                                  value={shownAmount(g, t, e)}
                                  placeholder={ahead ? 'Add more' : money(fee)}
                                  disabled={!editable}
                                  error={!!c.error}
                                  onChange={(ev) => typeAmount(g, t, e, ev.target.value)} />
                              </Box>
                            </Stack>
                            <StatusPills wide st={st} disabled={!editable}
                              onPick={(which) => pick(g, t, e, which)} />
                            {sent && <SubmittedChip wide />}
                          </>
                        )}

                        {c.error && (
                          <Typography color="error" sx={{ fontSize: 11, mt: 0.6 }}>{c.error}</Typography>
                        )}

                        {st && (
                          <Box sx={{ mt: 1 }}>
                            {st === 'noteaten' && !lock && (
                              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', mb: 0.4 }}>
                                WHY?
                              </Typography>
                            )}
                            <RemarkField wide st={st} value={shownRemark(g, t, e)} disabled={!editable}
                              onChange={(ev) => setRemark(g, t, e, ev.target.value)} />
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Card>
              );
            })}
          </Stack>
        </Box>
      ) : (

      /* ── The sheet, on a real screen ─────────────────────────── */
      <Card elevation={0} sx={{
        borderRadius: 2.5, overflow: 'hidden', mb: 1.5,
        border: '1px solid #e6ecf4',
      }}>
        <TableContainer sx={{ maxHeight: { xs: 'none', md: '64vh' } }}>
          <Table size="small" stickyHeader sx={{
            /* A full grid, not just row rules. Walking a class list of
               fifty, the eye needs the column boundary as much as the
               row one to stay on the right learner. */
            borderCollapse: 'separate', borderSpacing: 0,
            '& th': {
              bgcolor: '#eaf2fb',
              color: '#1e3a6b', fontWeight: 800, fontSize: 11.5,
              lineHeight: 1.35, py: 1,
              borderBottom: '1px solid #c9dbee',
              borderRight: '1px solid #d7e3f2',
              /* On a phone the page itself scrolls, so a sticky column
                 header would pin to the viewport and sit on top of the
                 summary bar. The summary is what has to stay; the column
                 headings can scroll away. */
              position: { xs: 'static', md: 'sticky' },
            },
            '& th:last-of-type': { borderRight: 0 },
            '& td': {
              fontSize: 12.5, color: '#1e293b',
              borderBottom: '1px solid #dde6f0', py: 0.7,
              borderRight: '1px solid #dde6f0',
            },
            '& td:last-of-type': { borderRight: 0 },
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 44 }}>#</TableCell>
                <TableCell sx={{ minWidth: 198 }}>Student Name</TableCell>
                <TableCell sx={{ width: 86 }}>Class</TableCell>
                <TableCell sx={{ width: 132 }}>Collection</TableCell>
                {/* The unit sits on its own line, as on the printed sheet
                    this replaces, so the column stays narrow. */}
                <TableCell align="center" sx={{ width: 92 }}>Fee<br />(GH¢)</TableCell>
                <TableCell align="center" sx={{ width: 118 }}>Payment Amount<br />(GH¢)</TableCell>
                <TableCell sx={{ width: 452 }}>Status / Action</TableCell>
                <TableCell sx={{ minWidth: 132 }}>Remarks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    {onlyCreditors
                      ? 'Nobody in this class is owing.'
                      : 'No students are registered for these collections in this class.'}
                  </TableCell>
                </TableRow>
              )}

              {groups.map((g, gi) => {
                // A learner's block is banded as a whole, so the second
                // and third collections read as the same person.
                const band = gi % 2 ? '#f6f9fd' : '#ffffff';
                const n = g.entries.length;
                const due = g.entries.reduce((a, x) => a + Number(x.entry.daily_amount || 0), 0);
                const owes = g.entries.reduce((a, x) => a + Number(x.entry.arrears || 0), 0);
                const held = g.entries.reduce((a, x) => a + Number(x.entry.credit || 0), 0);

                return g.entries.map(({ income_type: t, entry: e }, ei) => {
                  const k    = cellKey(g.student_id, t);
                  const c    = cell[k] || {};
                  const st   = shownStatus(g, t, e);
                  const fee  = Number(e.daily_amount || 0);
                  const tint = tintFor(allTypes, t);
                  const open = isOpen(t);
                  const ahead = paidAheadOn(e);
                  const lock = lockStateOf(e);
                  const sent = isSubmitted(e);
                  // Editable unless the day is shut or an approver has
                  // already ruled on this line. Being handed in is NOT a
                  // reason to lock it.
                  const editable = open && !lock;
                  const last = ei === n - 1;
                  // The rule under the LAST line of a block is heavier,
                  // so one learner is told from the next at a glance.
                  const rule = last
                    ? { borderBottom: '2px solid #c5d6e8' }
                    : {};

                  return (
                    <TableRow key={k} sx={{
                      bgcolor: band,
                      '& td': { ...rule },
                      '&:hover td': { bgcolor: '#e9f1fb' },
                    }}>
                      {ei === 0 && (
                        <>
                          {/* These three span the learner's whole block,
                              so they always end ON the block boundary and
                              carry the heavy rule regardless of which
                              line of the block they were written on. */}
                          <TableCell rowSpan={n} sx={{
                            color: '#64748b', fontWeight: 700, verticalAlign: 'top',
                            pt: 1.1, borderBottom: '2px solid #c5d6e8', ...TNUM,
                          }}>
                            {gi + 1}
                          </TableCell>
                          <TableCell rowSpan={n} sx={{
                            verticalAlign: 'top', pt: 1,
                            borderBottom: '2px solid #c5d6e8',
                          }}>
                            <Tooltip title={`${g.student_id}`
                              + (owes ? ` · owes GH¢ ${money(owes)}` : '')
                              + (held ? ` · credit GH¢ ${money(held)}` : '')}>
                              {/* Plain black. Blue reads as a link the
                                  teacher is meant to press, and there is
                                  nothing to press — the name is just the
                                  name. */}
                              <Typography component="span" sx={{
                                fontSize: 12.8, fontWeight: 700, color: '#0f172a',
                              }}>
                                {g.name}
                              </Typography>
                            </Tooltip>
                            {/* What the learner hands over in total, so
                                the teacher asks for one figure instead of
                                adding up the block in their head. */}
                            {n > 1 && (
                              <Typography sx={{
                                fontSize: 10.5, fontWeight: 700, color: '#1c5b96',
                                mt: 0.3, ...TNUM,
                              }}>
                                {n} collections · GH¢ {money(due)}
                              </Typography>
                            )}
                            {owes > 0 && (
                              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#b91c1c', ...TNUM }}>
                                Owing GH¢ {money(owes)}
                              </Typography>
                            )}
                            {n > 1 && (
                              <Stack direction="row" spacing={0.5} sx={{ mt: 0.6 }}>
                                <Chip size="small" label="All paid" clickable
                                  onClick={() => pickWholeRow(g, 'paid')}
                                  sx={{
                                    height: 20, fontSize: 9.5, fontWeight: 800,
                                    bgcolor: '#eaf7ef', color: '#15803d',
                                    border: '1px solid #b9e3ca',
                                  }} />
                                <Chip size="small" label="All credit" clickable
                                  onClick={() => pickWholeRow(g, 'credit')}
                                  sx={{
                                    height: 20, fontSize: 9.5, fontWeight: 800,
                                    bgcolor: '#fdf4e4', color: '#a1610a',
                                    border: '1px solid #f3ddb8',
                                  }} />
                              </Stack>
                            )}
                          </TableCell>
                          <TableCell rowSpan={n} sx={{
                            fontWeight: 600, color: '#334155',
                            verticalAlign: 'top', pt: 1.1,
                            borderBottom: '2px solid #c5d6e8',
                          }}>
                            {g.class_name}
                          </TableCell>
                        </>
                      )}

                      <TableCell>
                        <Chip size="small" label={prettyType(t)}
                          sx={{
                            height: 22, fontSize: 10.5, fontWeight: 800,
                            bgcolor: tint.bg, color: tint.fg,
                            border: `1px solid ${tint.line}`,
                          }} />
                      </TableCell>

                      {/* The money columns are what the round is actually
                          about — they carry the weight, not a light grey. */}
                      <TableCell align="center" sx={{ ...TNUM, fontWeight: 700, color: '#0f172a' }}>
                        {money(fee)}
                      </TableCell>

                      <TableCell align="center">
                        <AmountField
                          value={shownAmount(g, t, e)}
                          placeholder={ahead ? 'Add more' : money(fee)}
                          disabled={!editable}
                          error={!!c.error}
                          onChange={(ev) => typeAmount(g, t, e, ev.target.value)} />
                      </TableCell>

                      <TableCell>
                        {lock
                          ? <LockedChip state={lock} st={st} />
                          : (
                            <>
                              {ahead && <Box sx={{ mb: 0.5 }}><PaidAheadChip amount={e.credit_used} /></Box>}
                              <StatusPills st={st} disabled={!editable}
                                onPick={(which) => pick(g, t, e, which)} />
                            </>
                          )}
                        {sent && !lock && <Box><SubmittedChip /></Box>}
                        {c.error && (
                          <Typography color="error" sx={{ fontSize: 10, mt: 0.3 }}>{c.error}</Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <RemarkField st={st} value={shownRemark(g, t, e)} disabled={!editable}
                          onChange={(ev) => setRemark(g, t, e, ev.target.value)} />
                      </TableCell>
                    </TableRow>
                  );
                });
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      )}

      {/* ── What is blocking the submission ─────────────────────────
           Naming the classes, and letting the teacher jump straight to
           one. "Something is unmarked somewhere" sends a teacher through
           every class one at a time looking for it. */}
      {!dayComplete && (sheet?.students || []).length > 0 && (
        <Card elevation={0} sx={{
          p: 1.7, mb: 1.5, borderRadius: 2.5,
          bgcolor: classMarked ? '#eef4fb' : '#fdf6e7',
          border: `1px solid ${classMarked ? '#dce7f5' : '#f2e2bd'}`,
        }}>
          <Stack direction="row" spacing={1.3} alignItems="flex-start">
            <InfoIcon sx={{ fontSize: 20, color: classMarked ? '#2f6fb0' : '#d99b20', mt: 0.1 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{
                fontSize: 12.5, fontWeight: 800,
                color: classMarked ? '#1e3a6b' : '#a16207',
              }}>
                {classMarked
                  ? `${className || 'This class'} is finished — you can hand it in now`
                  : `${unmarkedHere.length} entr${unmarkedHere.length === 1 ? 'y' : 'ies'} `
                    + `in ${className || 'this class'} still to mark`}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.9 }}>
                {classMarked
                  ? 'These classes are still outstanding. They wait for you, not for this one — '
                    + 'tap a class to go to it.'
                  : requireAll
                    ? 'Every student has to be answered for: paid, part paid, credit, or not eaten. '
                      + 'Tap a class to go straight to it.'
                    : 'Your school allows a round to be handed in unfinished, so you can submit '
                      + 'what you have — the unmarked students simply will not be part of it. '
                      + 'Tap a class to go straight to it.'}
              </Typography>
              {/* The way out of the problem, not just a description of
                  it. One press and the sheet holds only the learners
                  keeping Submit shut. */}
              {unmarkedHere.length > 0 && (
                <Button size="small" variant="contained" disableElevation
                  startIcon={<UnmarkedIcon />}
                  onClick={showOnlyUnmarked}
                  sx={{
                    mb: 1.1, textTransform: 'none', fontWeight: 700, fontSize: 12.5,
                    px: 2, py: 0.9, borderRadius: 2,
                    bgcolor: '#d99b20', '&:hover': { bgcolor: '#a16207' },
                  }}>
                  Show me the {unmarkedHere.length} not marked
                </Button>
              )}

              <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                {unmarkedByClass.map(u => (
                  <Chip key={u.cls} size="small" clickable
                    label={`${u.cls} — ${u.students} student${u.students === 1 ? '' : 's'}`}
                    onClick={() => {
                      setClassName(u.cls); setChosen(null);
                      setOnlyCreditors(false);
                      // Jumping to another class must not carry this
                      // class's pinned list across with it.
                      setPinned(null); setPinnedWhy('');
                    }}
                    sx={{
                      height: 24, fontSize: 11, fontWeight: 700,
                      bgcolor: u.cls === className ? '#f5d78e' : '#fff',
                      color: '#a16207', border: '1px solid #e8c77a',
                    }} />
                ))}
              </Stack>
            </Box>
          </Stack>
        </Card>
      )}

      {/* ── Action guide + the two buttons ──────────────────────── */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="stretch">
        <Card elevation={0} sx={{
          p: 1.7, borderRadius: 2.5, flex: 1,
          bgcolor: '#eef4fb', border: '1px solid #dce7f5',
        }}>
          <Stack direction="row" spacing={1.3}>
            <InfoIcon sx={{ fontSize: 19, color: '#2f6fb0', mt: 0.2 }} />
            <Box>
              <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#1e3a6b', mb: 0.4 }}>
                Action Guide
              </Typography>
              {[
                ['PAID', 'Student took it and the full fee is in hand.'],
                ['PART PAID', 'Some of the fee now — opens on half, overtype it. '
                            + 'The shortfall is owed.'],
                ['CREDIT', 'Student took it but payment not received.'],
                ['NOT EATEN', 'Student did not take it. Say why — it opens on "Absent".'],
                ['Remarks', 'Writes itself from the status. Only NOT EATEN asks you.'],
                ['All paid / All credit',
                 'Marks every collection on that student in one press.'],
                ['After submitting',
                 'You can still correct a student — a late arrival who pays, say. '
               + 'The change goes back into the class, and you hand it in again.'],
                ['Locked',
                 'Only once an approver has approved, rejected or reversed it. '
               + 'That needs them to reverse it, not you.'],
              ].map(([k, v]) => (
                <Typography key={k} sx={{ fontSize: 11, color: '#64748b', lineHeight: 1.7 }}>
                  • <Box component="b" sx={{ color: '#334155' }}>{k}</Box> – {v}
                </Typography>
              ))}
            </Box>
          </Stack>
        </Card>

        <Stack direction="row" spacing={1.2} alignItems="center"
          sx={{ flexShrink: 0, justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
          <Tooltip title="See who is owing, and which days they took it without paying">
            <span>
              <Button size="large" variant="contained" disableElevation
                startIcon={<CreditorsIcon />}
                onClick={() => setCreditorsOpen(true)}
                sx={{
                  textTransform: 'none', fontWeight: 700, fontSize: 14,
                  px: 3, py: 1.25, borderRadius: 2, whiteSpace: 'nowrap',
                  bgcolor: '#2f6fb0', '&:hover': { bgcolor: '#1e3a6b' },
                }}>
                Creditors{creditorCount ? ` (${creditorCount})` : ''}
              </Button>
            </span>
          </Tooltip>
          {/* Submit is NOT repeated here. It lives in the pinned bar at
              the top, beside the figure it is submitting. */}
        </Stack>
      </Stack>

      <CreditorsWeekDialog
        open={creditorsOpen}
        onClose={() => setCreditorsOpen(false)}
        date={date}
        className={className}
        incomeType={types.length === 1 ? types[0] : ''}
        onFilterSheet={(ids) => {
          setPinned(ids);
          setPinnedWhy('from the creditors list');
          setSearch('');
        }} />
    </Box>
  );
}
